/**
 * Signature API Repository
 *
 * サイン登録に関するAPI通信を担当
 * Doctor側と同様のフロー: postFiles → S3アップロード → postSign
 */

import { ENV } from '../../../shared/config/env';
import { createApiClient } from '../../../shared/services/api';
import type { ApiResult } from '../../../shared/types/api';

// 環境変数からAPIベースURLを取得（未設定時はモック）
const API_BASE_URL = ENV.API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

/**
 * デバッグログをFlutter WebViewに送信
 */
const sendDebugLog = (message: string): void => {
  if (!ENV.DEBUG) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[Signature]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

/**
 * サイン種別
 */
export type SignType = 'estimate' | 'complete' | 'disclaime';

/**
 * サイン種別に応じたファイル名を取得
 */
const getSignFileName = (signType: SignType): string => {
  switch (signType) {
    case 'disclaime':
      return 'disclaimer_sign.png';
    case 'estimate':
      return 'estimate_sign.png';
    case 'complete':
      return 'complete_sign.png';
    default:
      return 'sign.png';
  }
};

/**
 * ファイル情報
 */
interface FileInfo {
  fileId: string;
  fileName: string;
  url: string;
}

/**
 * ファイルURL発行APIレスポンス
 */
interface PostFilesApiResponse {
  files: FileInfo[];
}

/**
 * サイン登録APIレスポンス
 */
interface SignRegisterApiResponse {
  signId: string;
}

/**
 * Base64データをBlobに変換
 */
const base64ToBlob = (base64Data: string): Blob => {
  // data:image/png;base64, のプレフィックスを削除
  const base64String = base64Data.replace(/^data:image\/\w+;base64,/, '');
  const byteCharacters = atob(base64String);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: 'image/png' });
};

/**
 * Signature Repository
 */
export const signatureRepository = {
  /**
   * ファイルアップロード用URL発行
   * @param fileName ファイル名
   * @param token 認証トークン
   * @returns fileId と uploadUrl
   */
  postFiles: async (
    fileName: string,
    token?: string
  ): Promise<ApiResult<{ fileId: string; uploadUrl: string }>> => {
    if (!API_BASE_URL) {
      sendDebugLog('postFiles: API_BASE_URL未設定');
      return {
        success: false,
        error: { type: 'client', message: 'API_BASE_URL is not configured' },
      };
    }

    sendDebugLog(`postFiles: URL発行開始 fileName=${fileName}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.post<PostFilesApiResponse>('/api/v1/files', {
      files: [{ fileName }],
    });

    if (!result.success) {
      sendDebugLog(`postFiles: エラー ${JSON.stringify(result.error)}`);
      return result;
    }

    if (!result.data.files || result.data.files.length === 0) {
      sendDebugLog('postFiles: レスポンスにファイル情報がありません');
      return {
        success: false,
        error: { type: 'server', message: 'No file info in response' },
      };
    }

    const fileInfo = result.data.files[0];
    sendDebugLog(`postFiles: 成功 fileId=${fileInfo.fileId}`);
    return {
      success: true,
      data: { fileId: fileInfo.fileId, uploadUrl: fileInfo.url },
    };
  },

  /**
   * S3へファイルをアップロード
   * @param uploadUrl S3署名付きURL
   * @param blob アップロードするファイルBlob
   * @returns 成功/失敗
   */
  uploadToS3: async (uploadUrl: string, blob: Blob): Promise<ApiResult<void>> => {
    sendDebugLog(`uploadToS3: アップロード開始`);

    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': 'image/png',
        },
      });

      if (!response.ok) {
        const errorMessage = `HTTP ${response.status} error`;
        sendDebugLog(`uploadToS3: エラー ${errorMessage}`);
        return {
          success: false,
          error: { type: 'server', statusCode: response.status, message: errorMessage },
        };
      }

      sendDebugLog('uploadToS3: 成功');
      return { success: true, data: undefined };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network error';
      sendDebugLog(`uploadToS3: ネットワークエラー ${errorMessage}`);
      return {
        success: false,
        error: { type: 'network', message: errorMessage },
      };
    }
  },

  /**
   * サイン登録
   * @param inquiryId お問い合わせID
   * @param signType サイン種別
   * @param fileId アップロードしたファイルのID
   * @param token 認証トークン
   * @returns signId
   */
  registerSign: async (
    inquiryId: string,
    signType: SignType,
    fileId: string,
    token?: string
  ): Promise<ApiResult<string>> => {
    if (!API_BASE_URL) {
      sendDebugLog('registerSign: API_BASE_URL未設定');
      return {
        success: false,
        error: { type: 'client', message: 'API_BASE_URL is not configured' },
      };
    }

    sendDebugLog(
      `registerSign: 登録開始 inquiryId=${inquiryId}, signType=${signType}, fileId=${fileId}`
    );

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.post<SignRegisterApiResponse>('/api/v1/inquiries/sign', {
      inquiryId,
      signType,
      fileId,
    });

    if (!result.success) {
      sendDebugLog(`registerSign: エラー ${JSON.stringify(result.error)}`);
      return result;
    }

    sendDebugLog(`registerSign: 成功 signId=${result.data.signId}`);
    return { success: true, data: result.data.signId };
  },

  /**
   * サイン処理（URL発行 + S3アップロード + サイン登録）を一括実行
   * Doctor側と同様の3ステップ方式
   *
   * @param inquiryId お問い合わせID
   * @param signType サイン種別
   * @param base64Data Base64エンコードされた画像データ
   * @param token 認証トークン
   * @returns signId
   */
  submitSignature: async (
    inquiryId: string,
    signType: SignType,
    base64Data: string,
    token?: string
  ): Promise<ApiResult<string>> => {
    sendDebugLog(`submitSignature: 開始 inquiryId=${inquiryId}, signType=${signType}`);

    if (USE_MOCK) {
      // モックモード
      sendDebugLog('submitSignature: モックモード使用 (API_BASE_URL未設定)');
      await new Promise(resolve => setTimeout(resolve, 500));
      const mockSignId = `mock-sign-${Date.now()}`;
      sendDebugLog(`submitSignature: モックデータ返却 signId=${mockSignId}`);
      return { success: true, data: mockSignId };
    }

    // Step 1: ファイルアップロード用URL発行
    const fileName = getSignFileName(signType);
    const postFilesResult = await signatureRepository.postFiles(fileName, token);
    if (!postFilesResult.success) {
      return postFilesResult;
    }

    const { fileId, uploadUrl } = postFilesResult.data;

    // Step 2: S3へサイン画像をアップロード
    const blob = base64ToBlob(base64Data);
    const uploadResult = await signatureRepository.uploadToS3(uploadUrl, blob);
    if (!uploadResult.success) {
      return uploadResult;
    }

    // Step 3: サイン登録API呼び出し
    const registerResult = await signatureRepository.registerSign(
      inquiryId,
      signType,
      fileId,
      token
    );

    return registerResult;
  },
};
